import { getAllListItems } from '@cineamo/ui/src/helpers/get-all-list-items';

import { CinemaRoom, RoomObject, Seat, SeatCategory } from '@cineamo/ui/src/seat-map/typedefs';

import { CinemaRoomDto } from '../../models/cinema-room/CinemaRoomDto.types';
import { RoomObjectDto } from '../../models/room-object/RoomObjectDto.types';
import { SeatDto } from '../../models/seat/SeatDto.types';
import { SeatCategoryDto } from '../../models/seat-category/SeatCategoryDto.types';
import { SeatRowDto } from '../../models/seat-row/SeatRowDto.types';
import { ShowingDto } from '../../models/showing/ShowingDto.types';
import { getRoomObjects } from '../../resources/room-object/room-object-api';
import { RoomObjectApiEmbed } from '../../resources/room-object/room-object-api.embed';
import { getCinemaRoomById, setCinemaRoomShape } from '../cinema-room/cinema-room-api';
import {
    createSeat,
    deleteSeatById,
    getSeats,
    setSeatPosition,
    setSeatShape,
    setSeatTransform,
    updateSeatById
} from '../seat/seat-api';
import { SeatApiEmbed } from '../seat/seat-api.embed';
import { getSeatCategories } from '../seat-category/seat-category-api';
import { createSeatRow, getSeatRows } from '../seat-row/seat-row-api';

export class SeatMapApi {
    async loadRoom(id: ShowingDto['cinemaRoomId']): Promise<CinemaRoom> {
        const response = await getCinemaRoomById(id);

        return response ? this.roomFromDto(response) : null;
    }

    async loadRoomObjects(roomId: ShowingDto['cinemaRoomId']): Promise<RoomObject[]> {
        const roomObjects = await getAllListItems({
            queryFn: (page: number) =>
                getRoomObjects({
                    cinemaRoomId: roomId,
                    embed: [RoomObjectApiEmbed.Shape, RoomObjectApiEmbed.Transform],
                    per_page: 100,
                    page: page
                }),
            listAccessor: 'room-objects'
        });

        return roomObjects.map((roomObject) => this.roomObjectFromDto(roomObject)) ?? null;
    }

    async loadSeats(roomId: ShowingDto['cinemaRoomId']): Promise<Seat[]> {
        const seats = await getAllListItems({
            queryFn: (page: number) =>
                getSeats({
                    cinemaRoomId: roomId,
                    embed: [SeatApiEmbed.Shape, SeatApiEmbed.Transform],
                    page: page,
                    per_page: 100
                }),
            listAccessor: 'seats'
        });

        return seats.map((seat) => this.seatFromDto(seat)) ?? null;
    }

    async loadSeatCategories(cinemaId: ShowingDto['cinemaId']): Promise<SeatCategory[]> {
        const categories = await getAllListItems({
            queryFn: (page: number) => getSeatCategories({ cinemaId, page }),
            listAccessor: 'seat-categories'
        });

        return categories.map((category) => this.seatCategoryFromDto(category)) ?? null;
    }

    async updateRoom(room: CinemaRoom): Promise<boolean> {
        const response = await setCinemaRoomShape(room.id, {
            width: room.width,
            depth: room.depth,
            height: room.height
        });
        return 'id' in response;
    }

    async createSeat(seat: Seat, room: CinemaRoom): Promise<Seat['id']> {
        const response = await createSeat({
            cinemaRoom: room.id,
            seatCategory: seat.category?.id
        });
        seat.id = 'id' in response ? response.id : 0;

        if (seat.id) {
            this.saveSeatRelatedData(seat, room);
        }

        return seat.id;
    }

    async updateSeat(seat: Seat, room: CinemaRoom): Promise<boolean> {
        const response = await updateSeatById(seat.id, {
            cinemaRoom: room.id,
            seatCategory: seat.category?.id
        });
        seat.id = 'id' in response ? response.id : 0;

        if (seat.id) {
            this.saveSeatRelatedData(seat, room);
        }

        return !!seat.id;
    }

    async deleteSeat(seat: Seat): Promise<boolean> {
        return await deleteSeatById(seat.id);
    }

    private saveSeatRelatedData(seat: Seat, room: CinemaRoom) {
        this.setSeatShape(seat).then();
        this.setSeatTransform(seat).then();

        this.findOrCreateRow(seat.rowName, room).then((rowDto) => {
            if (rowDto) {
                this.setSeatPosition(seat, rowDto).then();
            }
        });
    }

    private async findOrCreateRow(rowName: Seat['rowName'], room: CinemaRoom): Promise<SeatRowDto> {
        if (!rowName) return null;

        const response = await getSeatRows({
            cinemaRoomId: room.id,
            name: rowName,
            perPage: 1
        });
        let row = response?._embedded?.['seat-rows']?.[0] ?? null;

        if (!row) {
            const response = await createSeatRow({
                name: rowName,
                cinemaRoom: room.id
            });
            if ('id' in response) {
                row = response;
            }
        }

        return row;
    }

    private async setSeatShape(seat: Seat) {
        const response = await setSeatShape(seat.id, {
            width: seat.width,
            depth: seat.depth,
            height: seat.height
        });
        if (!('id' in response)) {
            console.error(response);
        }
    }

    private async setSeatTransform(seat: Seat) {
        const response = await setSeatTransform(seat.id, {
            seat: seat.id,
            positionX: seat.position.x,
            positionY: seat.position.y,
            positionZ: seat.position.z,
            rotationX: seat.rotation.x,
            rotationY: seat.rotation.y,
            rotationZ: seat.rotation.z
        });
        if (!('id' in response)) {
            console.error(response);
        }
    }

    private async setSeatPosition(seat: Seat, row: SeatRowDto) {
        if (!seat.positionName) return;

        const response = await setSeatPosition(seat.id, {
            name: seat.positionName,
            seatRow: row.id
        });
        if (!('id' in response)) {
            console.error(response);
        }
    }

    public roomFromDto(roomDto: CinemaRoomDto): CinemaRoom {
        const cinemaRoomShape = roomDto._embedded?.cinemaRoomShape;

        return {
            id: roomDto.id,
            cinemaId: roomDto.cinemaId,
            name: roomDto.name,
            basePrice: roomDto.basePrice,
            width: cinemaRoomShape?.width ?? 15000,
            depth: cinemaRoomShape?.depth ?? 20000,
            height: cinemaRoomShape?.height ?? 5000,
            dto: roomDto
        };
    }

    public roomObjectFromDto(roomObjectDto: RoomObjectDto): RoomObject {
        const roomObjectShape = roomObjectDto._embedded?.shape;

        return {
            id: roomObjectDto.id,
            name: roomObjectDto.name,
            width: roomObjectShape?.width ?? 1000,
            depth: roomObjectShape?.depth ?? 1000,
            height: roomObjectShape?.height ?? 1000,
            type: roomObjectDto.type ?? null,
            dto: roomObjectDto
        };
    }

    private seatFromDto(seatDto: SeatDto): Seat {
        const seatShape = seatDto._embedded.seatShape;
        const seatTransform = seatDto._embedded.seatTransform;
        const seatCategory = seatDto._embedded.seatCategory;
        const seatPosition = seatDto._embedded.seatPosition;

        const height = seatShape?.height ?? 1000;

        return {
            id: seatDto.id,
            width: seatShape?.width ?? 1000,
            type: seatDto.type,
            height,
            depth: seatShape?.depth ?? 1000,
            position: {
                x: seatTransform?.positionX ?? 0,
                y: seatTransform?.positionY ?? -height,
                z: seatTransform?.positionZ ?? 0
            },
            rotation: {
                x: seatTransform?.rotationX ?? 0,
                y: seatTransform?.rotationY ?? 0,
                z: seatTransform?.rotationZ ?? 0
            },
            category: seatCategory ? this.seatCategoryFromDto(seatCategory) : undefined,
            rowName: seatPosition?._embedded?.seatRow?.name ?? '',
            positionName: seatPosition?.name ?? 0
        };
    }

    private seatCategoryFromDto(seatCategoryDto: SeatCategoryDto): SeatCategory {
        return {
            id: seatCategoryDto.id,
            name: seatCategoryDto.name,
            priceChange: seatCategoryDto.priceChange,
            color: seatCategoryDto.color
        };
    }
}
